import { MARKETPLACE_TO_REALM, REALMS } from "./marketplaceUtils";
import { BETA_STAGE, GAMMA_STAGE, PROD_STAGE } from "../constants";

export const createPaystationLink = (
  marketplaceId: string,
  paymentContractRevisionId: string
) => {
  const realm = MARKETPLACE_TO_REALM[marketplaceId].toLowerCase();
  return `https://paystation-console-${realm}.aka.amazon.com/pcds?api=getPCRById&pcrId=${encodeURIComponent(
    paymentContractRevisionId
  )}`;
};

const getFlashUIBaseUrl = (marketplaceId: string, selectedStage: string) => {
  let realm = MARKETPLACE_TO_REALM[marketplaceId];

  if (selectedStage === BETA_STAGE) {
    realm = REALMS.NA; // Flare beta redirects all traffic to NA
  }

  const stage =
    selectedStage === PROD_STAGE ? "" : `-${selectedStage.toLowerCase()}`;
  const alias = selectedStage === BETA_STAGE ? "integ" : "aka";
  const domain = selectedStage === PROD_STAGE ? "" : "&domain=uat";
  const baseUrl = `https://flashui${stage}-${realm}.${alias}.amazon.com/search`;

  return { realm, baseUrl, domain };
};

export const createFlashUILink = (
  marketplaceId: string,
  orderId: string,
  selectedStage: string
) => {
  const { baseUrl } = getFlashUIBaseUrl(marketplaceId, selectedStage);
  return `${baseUrl}/transaction?transactionId=${encodeURIComponent(orderId)}`;
};

export const createFlashUIMatcherLink = (
  marketplaceId: string,
  rcslEntrySourceId: string,
  selectedStage: string
) => {
  const { baseUrl, realm, domain } = getFlashUIBaseUrl(
    marketplaceId,
    selectedStage
  );
  return `${baseUrl}/matcher?matchKey=${encodeURIComponent(
    rcslEntrySourceId
  )}&accountNumber=23110&realm=${realm}Amazon${domain}`;
};

export const createDigiConLink = (marketplaceId: string, orderId: string) => {
  const realm = MARKETPLACE_TO_REALM[marketplaceId];
  if (realm !== REALMS.NA) {
    const realmSuffix = `${realm.toUpperCase()}Amazon`;
    return `https://digicon-${realm}.amazon.com/order-dumper?orderID=${orderId}&realm=${realmSuffix}`;
  }
  return `https://digicon.amazon.com/order-dumper?orderID=${orderId}`;
};

export const createRaceLink = (
  marketplaceId: string,
  deferralId: string,
  selectedStage: string
) => {
  const namespace = selectedStage === PROD_STAGE ? "prod" : "uat";
  if (selectedStage === BETA_STAGE) {
    return `https://race-website.integ.amazon.com:13743/deferral_search/index?deferral-id=${encodeURIComponent(
      deferralId
    )}&namespace=test`;
  }
  const realm = MARKETPLACE_TO_REALM[marketplaceId];
  return `https://race-website-${realm}.aka.amazon.com/deferral_search/index?deferral-id=${encodeURIComponent(
    deferralId
  )}&namespace=${namespace}`;
};

export const createSubsLink = (
  stage: string,
  marketplaceId: string,
  subscriptionId: string
) => {
  if (!subscriptionId || stage === GAMMA_STAGE) return null;
  const realm = MARKETPLACE_TO_REALM[marketplaceId];
  if (stage === PROD_STAGE)
    return `https://subscriptions-${realm}.amazon.com/#/subscription/${subscriptionId}`;
  return `https://subscriptions-${realm}-beta.integ.amazon.com/#/subscription/${subscriptionId}`;
};

export const createTaxLink = (
  stage: string,
  marketplaceId: string,
  taxId: string
): string => {
  const realm = MARKETPLACE_TO_REALM[marketplaceId]?.toUpperCase();
  const subdomain = stage === BETA_STAGE ? "beta." : "";
  return `https://${subdomain}console.tax.amazon.dev/tarviewer/search/DigitalTransactionId/${taxId}/${realm}ECP/default`;
};

const getHerdBaseUrl = (
  marketplaceId: string,
  stage: string
): { baseUrl: string; client: string } => {
  const CLIENTS = {
    PROD: "gfs-calvin-herd",
    UAT: "gfs-calvin-herd-uat",
    BETA: "gfs-calvin-herd",
  };

  const realm = MARKETPLACE_TO_REALM[marketplaceId];

  const regionMap: Record<string, string> = {
    [REALMS.NA]: "us-east-1",
    [REALMS.EU]: "eu-west-1",
    [REALMS.FE]: "us-west-2",
  };

  if (stage === BETA_STAGE) {
    return {
      baseUrl: `https://beta.us-west-2.studio.orca.amazon.dev`,
      client: CLIENTS.BETA,
    };
  }

  const region = regionMap[realm];

  const client = stage === PROD_STAGE ? CLIENTS.PROD : CLIENTS.UAT;

  return {
    baseUrl: `https://${region}.studio.orca.amazon.dev`,
    client,
  };
};

export const createHerdLink = (
  cbe: {
    businessNamespace: string;
    idempotenceId: string;
    idempotenceVersion: number;
  },
  marketplaceId: string,
  stage: string
): string => {
  const { baseUrl, client } = getHerdBaseUrl(marketplaceId, stage);
  const executionPath = `${cbe.businessNamespace}.${cbe.idempotenceId}.${cbe.idempotenceVersion}`;

  return `${baseUrl}/#/clients/${client}/executions/${executionPath}`;
};
